import * as React from "react";
import { useEffect, useState } from "react";
import RadioButton from "../commons/components/buttons/RadioButton";
import ReactCompareImage from "react-compare-image";
import ToggleSwitch, { SelectedOption } from "../commons/components/buttons/ToggleSwitch";
import Scan, { MouthDescriptor } from "../commons/model/Scan";
import { renderMultilineText } from "../commons/utils/text-rendering";
import SmileSimulationTreatmentSettings, {
  isScanEnabled,
} from "../settings/SmileSimulationTreatmentSettings";
import "./AssessmentResultSmileSimulation.scss";
import { SmileSimulationPhotoCategory } from "../commons/model/report/Report";
import { sanitize } from "../commons/utils/sanitize";

interface AssessmentResultSmileSimulationProps {
  smileWithApplianceScans: Scan[];
  smilePredictionScans: Scan[];
  smileWithApplianceAvailable: boolean;
  smileWithApplianceErrorDescription?: string;
  smilePredictionAvailable: boolean;
  smilePredictionErrorDescription?: string;
  initialSmileWithAppliancePicture?: Scan;
  initialSmilePredictionPicture?: Scan;
  isPatient: boolean;
  settings: SmileSimulationTreatmentSettings;
  flowUrl?: string;
  canRetry: boolean;
}

interface CategoryConfiguration {
  title?: string;
  onChange: (scan: Scan) => void;
  scans: Scan[];
  switchPosition: SelectedOption;
  initialPictureHref?: string;
}

export default function AssessmentResultSmileSimulation({
  smileWithApplianceScans,
  smilePredictionScans,
  smileWithApplianceAvailable,
  smileWithApplianceErrorDescription,
  smilePredictionAvailable,
  smilePredictionErrorDescription,
  initialSmileWithAppliancePicture,
  initialSmilePredictionPicture,
  isPatient,
  settings,
  flowUrl,
  canRetry,
}: AssessmentResultSmileSimulationProps) {
  const [categoryConfigurations, setCategoryConfigurations] =
    useState<Map<SmileSimulationPhotoCategory, CategoryConfiguration>>();
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [selectedSmileWithApplianceScan, setSelectedSmileWithApplianceScan] = useState<Scan>();
  const [selectedSmilePredictionScan, setSelectedSmilePredictionScan] = useState<Scan>();
  const [categoryConfiguration, setCategoryConfiguration] = useState<CategoryConfiguration>();
  const [selectedScan, setSelectedScan] = useState<Scan>();

  useEffect(() => {
    const initialSelectedCategory = smilePredictionAvailable
      ? SmileSimulationPhotoCategory.SMILE_PREDICTION
      : SmileSimulationPhotoCategory.SMILE_WITH_APPLIANCE;
    setSelectedCategory(initialSelectedCategory);
    const initialCategoryConfigurations = new Map<
      SmileSimulationPhotoCategory,
      CategoryConfiguration
    >();
    initialCategoryConfigurations[SmileSimulationPhotoCategory.SMILE_WITH_APPLIANCE] = {
      onChange: (scan: Scan) => {
        setSelectedSmileWithApplianceScan(scan);
      },
      scans: smileWithApplianceScans.filter((scan) => isScanEnabled(scan.descriptor, settings)),
      switchPosition: SelectedOption.LEFT,
      initialPictureHref: buildScanHref(initialSmileWithAppliancePicture),
    };
    initialCategoryConfigurations[SmileSimulationPhotoCategory.SMILE_PREDICTION] = {
      onChange: (scan: Scan) => {
        setSelectedSmilePredictionScan(scan);
      },
      scans: smilePredictionScans.filter((scan) => isScanEnabled(scan.descriptor, settings)),
      switchPosition: SelectedOption.RIGHT,
      initialPictureHref: buildScanHref(initialSmilePredictionPicture),
    };
    setCategoryConfigurations(initialCategoryConfigurations);
    setSelectedSmileWithApplianceScan(
      initialCategoryConfigurations[SmileSimulationPhotoCategory.SMILE_WITH_APPLIANCE].scans[0]
    );
    setSelectedSmilePredictionScan(
      initialCategoryConfigurations[SmileSimulationPhotoCategory.SMILE_PREDICTION].scans[0]
    );
    setCategoryConfiguration(initialCategoryConfigurations[initialSelectedCategory]);
  }, []);

  useEffect(() => {
    if (categoryConfigurations) {
      setCategoryConfiguration(categoryConfigurations[selectedCategory]);
    }
  }, [selectedCategory]);

  useEffect(() => {
    setSelectedScan(
      selectedCategory === SmileSimulationPhotoCategory.SMILE_WITH_APPLIANCE
        ? selectedSmileWithApplianceScan
        : selectedSmilePredictionScan
    );
  }, [selectedCategory, selectedSmilePredictionScan, selectedSmileWithApplianceScan]);

  const buildScanHref = (scan?: Scan) => {
    if (!scan) {
      return undefined;
    }
    return `${scan.href}`;
  };

  // won't be needed anymore when fix is deployed in future react-image version
  // https://github.com/tam315/react-compare-image/pull/129
  useEffect(() => {
    if (typeof window.TouchEvent === "undefined") {
      (window as any).TouchEvent = class TouchEvent extends Event {};
    }
  }, []);

  const getI18nDescription = (descriptor: MouthDescriptor): string => {
    switch (descriptor) {
      case MouthDescriptor.FACE_SMILING_AFTER_TREATMENT:
        return "Page.AssessmentResult.SmileSimulation.Treatment.Outcome.Alignment";
      case MouthDescriptor.FACE_SMILING_AFTER_TREATMENT_WITH_WHITENING:
        return "Page.AssessmentResult.SmileSimulation.Treatment.Outcome.AlignmentWithWhitening";
      case MouthDescriptor.FACE_SMILING_WITH_WHITENING:
        return "Page.AssessmentResult.SmileSimulation.Treatment.Outcome.Whitening";
      case MouthDescriptor.FACE_MOUTH_SLIGHTLY_OPENED_ALIGNERS:
        return "Page.AssessmentResult.SmileSimulation.Treatment.Type.Aligners";
      case MouthDescriptor.FACE_MOUTH_SLIGHTLY_OPENED_METALLIC_BRACES:
        return "Page.AssessmentResult.SmileSimulation.Treatment.Type.MetallicBraces";
      case MouthDescriptor.FACE_MOUTH_SLIGHTLY_OPENED_SELF_LIGATING_BRACES:
        return "Page.AssessmentResult.SmileSimulation.Treatment.Type.SelfLigatingBraces";
      case MouthDescriptor.FACE_MOUTH_SLIGHTLY_OPENED_CERAMIC_BRACES:
        return "Page.AssessmentResult.SmileSimulation.Treatment.Type.CeramicBraces";
      case MouthDescriptor.OPENED_TOP:
      case MouthDescriptor.OPENED_BOTTOM:
      case MouthDescriptor.CLOSED_LEFT:
      case MouthDescriptor.CLOSED_FRONT:
      case MouthDescriptor.CLOSED_RIGHT:
      case MouthDescriptor.SLIGHTLY_OPENED_LEFT:
      case MouthDescriptor.SLIGHTLY_OPENED_FRONT_LEFT:
      case MouthDescriptor.SLIGHTLY_OPENED_FRONT:
      case MouthDescriptor.SLIGHTLY_OPENED_FRONT_RIGHT:
      case MouthDescriptor.SLIGHTLY_OPENED_RIGHT:
      case MouthDescriptor.FACE_SMILING:
      case MouthDescriptor.FACE_MOUTH_SLIGHTLY_OPENED:
      case MouthDescriptor.FACE_SMILING_CORRECTED:
      case MouthDescriptor.FACE_MOUTH_SLIGHTLY_OPENED_CORRECTED:
        throw Error("Unhandled (not translated) MouthDescriptor: " + descriptor);
    }
  };

  const retry = () => {
    if (flowUrl !== null) {
      document.location.href = flowUrl;
    }
  };

  return (
    <div className="category panel with-shadow">
      <h1 className="title">{i18n["Page.AssessmentResult.SmileSimulation.Title"]}</h1>
      <div className="message smile-simulation-info">
        {i18n["Page.AssessmentResult.SmileSimulation.Information"]}
      </div>
      {!smilePredictionAvailable && !smileWithApplianceAvailable ? (
        <div className="h-flex centered">
          <div className="message info">
            <div>
              {renderMultilineText(
                i18n["Page.AssessmentResult.SmileSimulation.Error.SmileSimulationUnavailable"]
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="smile-simulation">
          <div className="slider">
            {categoryConfiguration && selectedScan && (
              <ReactCompareImage
                skeleton={
                  <div className="slider-skeleton h-flex centered">
                    <div className="slider-loader" />
                  </div>
                }
                leftImage={categoryConfiguration.initialPictureHref}
                leftImageCss={{ borderRadius: ".5rem" }}
                rightImage={buildScanHref(selectedScan)}
                rightImageCss={{ borderRadius: ".5rem" }}
                sliderLineWidth={4}
                sliderLineColor="var(--primary-color-2)"
                aspectRatio="wider"
                handle={
                  <div className="slider-handle">
                    <i className="ic_sort" />
                  </div>
                }
              />
            )}
          </div>
          <div className="selection">
            <div className="category-title">
              {i18n["Page.AssessmentResult.SmileSimulation.CategoryTitle"]}
            </div>
            <div className="toggle-switch-container">
              {categoryConfiguration && (
                <ToggleSwitch
                  leftLabel={i18n["Page.AssessmentResult.SmileSimulation.SmileWithApplianceLabel"]}
                  leftDisabled={!smileWithApplianceAvailable}
                  rightLabel={i18n["Page.AssessmentResult.SmileSimulation.SmilePredictionLabel"]}
                  rightDisabled={!smilePredictionAvailable}
                  optionSelected={categoryConfiguration?.switchPosition}
                  onSwitch={(selected) =>
                    setSelectedCategory(
                      selected === SelectedOption.RIGHT
                        ? SmileSimulationPhotoCategory.SMILE_WITH_APPLIANCE
                        : SmileSimulationPhotoCategory.SMILE_PREDICTION
                    )
                  }
                />
              )}
            </div>
            {!smilePredictionAvailable ? (
              <div className="unavailable-category-message message">
                <div className="message">
                  {isPatient
                    ? i18n[
                        "Page.AssessmentResult.SmileSimulation.Error.SmilePredictionUnavailable.Patient"
                      ]
                    : i18n[
                        "Page.AssessmentResult.SmileSimulation.Error.SmilePredictionUnavailable.Doctor"
                      ]}
                </div>
                <div
                  className="message-inner-content"
                  dangerouslySetInnerHTML={{
                    __html: sanitize(smilePredictionErrorDescription),
                  }}
                />
                {canRetry && (
                  <div className="message restore" onClick={retry}>
                    {i18n["Page.AssessmentResult.SmileSimulation.Error.Retry"]}
                  </div>
                )}
              </div>
            ) : (
              !smileWithApplianceAvailable && (
                <div className="unavailable-category-message message">
                  <div className="message">
                    {isPatient
                      ? i18n[
                          "Page.AssessmentResult.SmileSimulation.Error.SmileWithApplianceUnavailable.Patient"
                        ]
                      : i18n[
                          "Page.AssessmentResult.SmileSimulation.Error.SmileWithApplianceUnavailable.Doctor"
                        ]}
                  </div>
                  <div
                    className="message-inner-content"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(smileWithApplianceErrorDescription),
                    }}
                  />
                  {canRetry && (
                    <div className="message restore" onClick={retry}>
                      {i18n["Page.AssessmentResult.SmileSimulation.Error.Retry"]}
                    </div>
                  )}
                </div>
              )
            )}
            <div className="choice-selection">
              {categoryConfiguration &&
                categoryConfiguration.scans.map((scan) => (
                  <RadioButton
                    name="scansSelection"
                    value={scan.descriptor}
                    id={scan.uid}
                    checked={selectedScan?.uid === scan.uid}
                    label={i18n[getI18nDescription(scan.descriptor)]}
                    onChange={() => categoryConfiguration.onChange(scan)}
                    disabled={categoryConfiguration.scans.length == 1}
                    key={scan.uid}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
